import React, { useEffect, useState } from "react";
import Style from "../style/city.module.scss";
import { useAppDispatch } from "../redux/hooks";
import { fetchStates } from "../redux/Slice";
import { getStatesFromDB, saveStatesToDB } from "../indexDb/stateDb";
import staticCities from "../json/state.json";

const defaultCityImage =
  "https://149990825.v2.pressablecdn.com/wp-content/uploads/2023/09/NewYork1.jpg";
const baseUrl = "https://backend.onedialusa.com/assets/img/location/city/";

const buildImgUrl = (img: string, width: number, height: number) => {
  const hasQuery = img.includes("?");
  const connector = hasQuery ? "&" : "?";
  return `${baseUrl}${img}${connector}w=${width}&h=${height}&format=webp&quality=70`;
};

const buildFallbackUrl = (img: string, width: number, height: number) => {
  const hasQuery = img.includes("?");
  const connector = hasQuery ? "&" : "?";
  return `${baseUrl}${img}${connector}w=${width}&h=${height}&format=jpeg&quality=70`;
};

const City = React.memo(() => {
  const dispatch = useAppDispatch();
  const [localCities, setLocalCities] = useState<any[]>(
    staticCities.slice(0, 14)
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const cachedData = await getStatesFromDB();
        const cachedCities = cachedData.flatMap((state) => state.cities || []);

        if (cachedCities.length > 0) {
          setLocalCities(cachedCities.slice(0, 14));
          preloadImages(cachedCities.slice(0, 14));
        } else {
          setLocalCities(staticCities.slice(0, 14));
          preloadImages(staticCities.slice(0, 14));
        }
        const alreadyFetched = localStorage.getItem("states_fetched");
        if (!alreadyFetched) {
          const response = await dispatch(fetchStates()).unwrap();
          if (response?.states) {
            await saveStatesToDB(response.states);
            localStorage.setItem("states_fetched", "true");

            const newCities = response.states.flatMap(
              (state: any) => state.cities || []
            );
            setLocalCities(newCities.slice(0, 14));
            preloadImages(newCities.slice(0, 14));
          }
        }
      } catch (error) {
        console.error("Error fetching states:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dispatch]);

  const preloadImages = (cities: any) => {
    cities.slice(0, 3).forEach((city: any) => {
      if (!city.feature_image) return;
      const preload = document.createElement("link");
      preload.rel = "preload";
      preload.as = "image";
      preload.href = buildImgUrl(city.feature_image, 300, 200);
      preload.type = "image/webp";
      document.head.appendChild(preload);
      const img = new Image();
      img.src = buildImgUrl(city.feature_image, 300, 200);
      img.onload = () => {};
    });
  };

  const handleClick = () => {
    window.location.pathname = "/usa";
  };

  if (loading) {
    return (
      <div className={Style.loading}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className={Style.container}>
      <div className={Style.wrapper}>
        <div className={Style.title}>
          <h2>Explore Top Cities</h2>
          <div className={Style.button}>
            <span onClick={handleClick}>Show All Cities</span>
          </div>
        </div>

        <div className={Style.content}>
          {localCities.map((item) => (
            <a
              href={`/listings/city/${item.name
                .toLowerCase()
                .replaceAll(" ", "-")}`}
              key={item.id}
            >
              <span>
                <picture>
                  {item.feature_image && (
                    <>
                      <source
                        srcSet={`
                          ${buildImgUrl(item.feature_image, 150, 100)} 150w,
                          ${buildImgUrl(item.feature_image, 300, 200)} 300w,
                          ${buildImgUrl(item.feature_image, 600, 400)} 600w
                        `}
                        sizes="(max-width: 600px) 150px, (max-width: 1024px) 300px, 600px"
                        type="image/webp"
                      />

                      <source
                        srcSet={` 
                          ${buildFallbackUrl(
                            item.feature_image,
                            150,
                            100
                          )} 150w,
                          ${buildFallbackUrl(
                            item.feature_image,
                            300,
                            200
                          )} 300w,
                          ${buildFallbackUrl(item.feature_image, 600, 400)} 600w
                        `}
                        sizes="(max-width: 600px) 150px, (max-width: 1024px) 300px, 600px"
                        type="image/jpeg"
                      />
                    </>
                  )}
                  <img
                    src={
                      item.feature_image
                        ? buildFallbackUrl(item.feature_image, 150, 100)
                        : defaultCityImage
                    }
                    alt={`View of ${item.name}`}
                    loading="lazy"
                    decoding="async"
                    width="150"
                    height="100"
                    onError={(e) => {
                      const img = e.target as HTMLImageElement;
                      img.src = defaultCityImage;
                      img.srcset = "";
                    }}
                    style={{
                      objectFit: "cover",
                      width: "100%",
                      height: "100%",
                    }}
                  />
                </picture>
              </span>
              <div className={Style.overlay}>
                <h2>{item.name}</h2>
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
});

export default City;
